// Field geometry in file units, shared by canvas, interaction, alignment and export.
// Boxes come from the pure metrics/barcode modules so the canvas matches the printer model.
import { textBox, sampleDateText } from '../render/metrics.js';
import * as dates from '../itf/dates.js';
import * as barcode from '../itf/barcode.js';

export const UNSUPPORTED_BOX = Object.freeze({ x: 0, y: 0, w: 3200, h: 900 });
const MIN_HIT = 150; // 1.5 mm, so empty/tiny fields stay clickable

let nowCache = { t: 0, d: new Date() };
/** "Today" for previews, refreshed at most once a minute so boxes do not jitter. */
export function today() {
  const t = Date.now();
  if (t - nowCache.t > 60000) nowCache = { t, d: new Date() };
  return nowCache.d;
}

/** What the field prints right now (text fields: the text; date fields: the offset date). */
export function displayText(f) {
  if (!f) return '';
  if (f.kind === 'text') return String(f.text ?? '');
  if (f.kind === 'date') {
    try {
      return dates.formatDate(f.format, dates.applyOffset(today(), f.offset), f.language || 'ENGLISH');
    } catch {
      return sampleDateText(f.format);
    }
  }
  if (f.kind === 'barcode') return String(f.data ?? '');
  return '';
}

/** Unrotated box relative to the field origin. */
export function localBox(f) {
  if (!f) return { x: 0, y: 0, w: 0, h: 0 };
  if (f.kind === 'text' || f.kind === 'date') {
    const b = textBox(f, displayText(f));
    return { x: b.x || 0, y: b.y || 0, w: Math.max(b.w || 0, MIN_HIT), h: Math.max(b.h || 0, MIN_HIT) };
  }
  if (f.kind === 'barcode') {
    try {
      const b = typeof barcode.barcodeBox === 'function' ? barcode.barcodeBox(f) : null;
      if (b && Number.isFinite(b.w) && Number.isFinite(b.h)) {
        return { x: b.x || 0, y: b.y || 0, w: Math.max(b.w, MIN_HIT), h: Math.max(b.h, MIN_HIT) };
      }
    } catch {
      /* invalid data: fall through to a nominal box */
    }
    return { x: 0, y: 0, w: 3000, h: Math.max(Number(f.barHeight) || 1000, MIN_HIT) };
  }
  return { ...UNSUPPORTED_BOX };
}

/** SVG rotate(deg) in a y-down space. */
export function rotateVec(x, y, deg) {
  const r = ((Number(deg) || 0) * Math.PI) / 180;
  const c = Math.round(Math.cos(r) * 1e9) / 1e9;
  const s = Math.round(Math.sin(r) * 1e9) / 1e9;
  return { x: x * c - y * s, y: x * s + y * c };
}

/** Corners of the rotated box in label units. */
export function corners(f, box = localBox(f)) {
  const pts = [
    [box.x, box.y],
    [box.x + box.w, box.y],
    [box.x + box.w, box.y + box.h],
    [box.x, box.y + box.h],
  ];
  return pts.map(([x, y]) => {
    const p = rotateVec(x, y, f.rotation);
    return { x: (f.x || 0) + p.x, y: (f.y || 0) + p.y };
  });
}

/** Axis-aligned bounds in label units: { x, y, w, h }. */
export function fieldBounds(f, box) {
  const c = corners(f, box);
  const xs = c.map((p) => p.x);
  const ys = c.map((p) => p.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

export function unionBounds(list) {
  if (!list.length) return null;
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const b of list) {
    x0 = Math.min(x0, b.x);
    y0 = Math.min(y0, b.y);
    x1 = Math.max(x1, b.x + b.w);
    y1 = Math.max(y1, b.y + b.h);
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

export const intersects = (a, b) => a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
